import { ATLAS_DATA_CONTRACT_VERSION, ENGINE_RULESET_VERSION } from '../contracts/dataContract';

export interface EngineCapabilities {
  engineRulesetVersion: string;
  /** Data contract version the engine believes it is consuming */
  atlasDataContractVersion: string;
  supportedEnums: Record<string, string[]>;
  supportedInputs: Array<{ key: string; type?: string; required: boolean; enumRef?: string }>;
  engineOutputs: string[];
}

/**
 * Baked capability registry for devtools coverage diffing.
 *
 * This is intentionally explicit and human-auditable.
 *
 * Enum keys here are the ENGINE-side values. When canonical UI values differ
 * (e.g. sludgeRisk: 'none' → 'NONE') the atlasDataContract.ts FieldDef
 * documents the engineEnumMap and toEngineInput.ts applies it.
 */
export const engineCapabilities: EngineCapabilities = {
  engineRulesetVersion: ENGINE_RULESET_VERSION,
  atlasDataContractVersion: ATLAS_DATA_CONTRACT_VERSION,
  supportedEnums: {
    // ── Core system ──────────────────────────────────────────────────────────
    boilerType: ['combi', 'storage_combi', 'system', 'regular', 'air_source_hp', 'ground_source_hp', 'electric', 'storage_heaters', 'unknown'],
    waterSystem: ['on_demand', 'unvented', 'open_vented', 'mixergy_unvented', 'mixergy_open', 'heat_battery', 'thermal_store', 'unknown'],

    // ── Property ─────────────────────────────────────────────────────────────
    propertyType: ['flat', 'maisonette', 'terraced', 'end_terrace', 'semi', 'detached', 'bungalow', 'unknown'],
    propertyAgeBand: ['pre_1919', '1919_1944', '1945_1964', '1965_1980', '1981_2000', '2001_2010', '2011_plus', 'unknown'],

    // ── Occupancy ────────────────────────────────────────────────────────────
    occupancyTiming: ['mornings_evenings', 'evenings_weekends', 'most_of_day', 'variable', 'unknown'],
    temporaryGuests: ['none', 'occasional', 'frequent', 'permanent_addition'],

    // ── Mains / DHW ──────────────────────────────────────────────────────────
    coldMainType: ['mdpe', 'copper', 'lead', 'unknown'],

    // ── Electrical ───────────────────────────────────────────────────────────
    // Engine-side enum values (UI values differ — see electricalCapacity FieldDef).
    electricalCapacityConfidence: ['unknown', 'insufficient', 'likely_ok'],

    // ── Baseline ─────────────────────────────────────────────────────────────
    currentFuelType: ['gas', 'lpg', 'oil', 'electric'],
    // Engine expects UPPERCASE sludge risk values (UI canonical: none/possible/likely/confirmed).
    sludgeRisk: ['NONE', 'POSSIBLE', 'LIKELY', 'CONFIRMED'],
    // Engine source enum (UI canonical: sedbuk/epc/erp/unknown).
    efficiencySource: ['SEDBUK', 'EPC_BAND', 'USER', 'ASSUMED'],
    sedbukBand: ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
    epcBand: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'unknown'],

    // ── Emitters ─────────────────────────────────────────────────────────────
    emitterType: ['radiators', 'underfloor', 'mixed', 'unknown'],
    // Matches types.ts emitters.lowTempSuitability and performanceClaims.ts isLowTempConfirmed
    emitterReadiness: ['likely_ok', 'likely_needs_upgrade', 'definitely_needs_upgrade', 'unknown'],
    heatEmitterType: ['standard_rads', 'oversized_rads', 'underfloor_heating'],
    pipeBore: ['8mm', '10mm', '15mm', '22mm', 'unknown'],

    // ── Insulation ───────────────────────────────────────────────────────────
    // Engine uses high/medium/poor/unknown (UI canonical: good/average/poor/unknown).
    insulationLevel: ['unknown', 'poor', 'medium', 'high'],
    retrofitLoft: ['unknown', 'none', 'some', 'good'],
    retrofitWall: ['unknown', 'none', 'cavity', 'solid_or_internal_or_external'],
    retrofitWindows: ['unknown', 'single', 'older_double', 'modern_double_or_triple'],
    // Engine uses none/some/good (UI canonical: none/partial/modern/unknown).
    retrofitDraught: ['unknown', 'none', 'some', 'good'],

    // ── Controls / tariff ────────────────────────────────────────────────────
    controls: ['none', 'basic_programmer', 'smart_zonal', 'weather_comp', 'open_therm'],
    tariffProfile: ['flat', 'eco7', 'smart_tou', 'agile_like'],

    // ── Solar ────────────────────────────────────────────────────────────────
    solarIntent: ['none', 'planned', 'installed', 'unknown'],

    // ── Upgrade flags ────────────────────────────────────────────────────────
    requiredUpgradeFlags: ['emitters.low_temp', 'pipework.primary_resize', 'controls.zoning', 'mains.pressure_support'],
  },
  supportedInputs: [
    // ── Core (required) ──────────────────────────────────────────────────────
    { key: 'propertyType',            type: 'enum',   required: true,  enumRef: 'propertyType' },
    { key: 'propertyAgeBand',         type: 'enum',   required: true,  enumRef: 'propertyAgeBand' },
    { key: 'bedrooms',                type: 'number', required: true },
    { key: 'bathrooms',               type: 'number', required: true },
    { key: 'occupancy',               type: 'number', required: true },
    { key: 'hotWaterDemand',          type: 'enum',   required: true },
    { key: 'currentBoiler',           type: 'enum',   required: true,  enumRef: 'boilerType' },
    { key: 'currentWater',            type: 'enum',   required: true,  enumRef: 'waterSystem' },
    { key: 'currentFuelType',         type: 'enum',   required: true,  enumRef: 'currentFuelType' },

    // ── Occupancy ────────────────────────────────────────────────────────────
    { key: 'occupancyTiming',         type: 'enum',   required: false, enumRef: 'occupancyTiming' },
    { key: 'temporaryGuests',         type: 'enum',   required: false, enumRef: 'temporaryGuests' },
    { key: 'futureDemand',            type: 'enum',   required: false },

    // ── Mains / pressure ─────────────────────────────────────────────────────
    { key: 'mainsPressure',           type: 'number', required: false },
    { key: 'flowRate',                type: 'number', required: false },
    { key: 'mainsFlowLpm',            type: 'number', required: false },
    { key: 'mainsStaticPressureBar',  type: 'number', required: false },
    { key: 'mainsDynamicFlowLpm',     type: 'number', required: false },
    { key: 'mainsDynamicPressureBar', type: 'number', required: false },
    { key: 'coldMainType',            type: 'enum',   required: false, enumRef: 'coldMainType' },

    // ── Electrical ───────────────────────────────────────────────────────────
    // Engine-side key; UI sends electricalCapacity which adapter maps via engineEnumMap.
    { key: 'electricalCapacityConfidence', type: 'enum', required: false, enumRef: 'electricalCapacityConfidence' },

    // ── Space ────────────────────────────────────────────────────────────────
    { key: 'spaceAvailability',       type: 'enum',   required: false },

    // ── Plans ────────────────────────────────────────────────────────────────
    { key: 'expectedLengthOfStay',    type: 'enum',   required: false },
    { key: 'costPriority',            type: 'enum',   required: false },
    { key: 'disruptionTolerance',     type: 'enum',   required: false },
    { key: 'urgency',                 type: 'enum',   required: false },
    { key: 'upgradePriorities',       type: 'array',  required: false },

    // ── Baseline ─────────────────────────────────────────────────────────────
    { key: 'existingSystemAge',            type: 'number', required: false },
    { key: 'sludgeRisk',                   type: 'enum',   required: false, enumRef: 'sludgeRisk' },
    { key: 'currentSystemEfficiency.source',     type: 'enum', required: false, enumRef: 'efficiencySource' },
    { key: 'currentSystemEfficiency.sedbukBand', type: 'enum', required: false, enumRef: 'sedbukBand' },
    { key: 'currentSystemEfficiency.epcBand',    type: 'enum', required: false, enumRef: 'epcBand' },

    // ── Emitters ─────────────────────────────────────────────────────────────
    { key: 'emitters.type',              type: 'enum', required: false, enumRef: 'emitterType' },
    { key: 'emitters.lowTempSuitability', type: 'enum', required: false, enumRef: 'emitterReadiness' },
    { key: 'emitters.roomsStruggleToHeat', type: 'enum', required: false },
    { key: 'emitters.pipeBore',          type: 'enum', required: false, enumRef: 'pipeBore' },
    { key: 'heatEmitterType',            type: 'enum', required: false, enumRef: 'heatEmitterType' },

    // ── Insulation / retrofit ────────────────────────────────────────────────
    { key: 'insulationLevel',            type: 'enum', required: false, enumRef: 'insulationLevel' },
    { key: 'retrofit.loftInsulation',    type: 'enum', required: false, enumRef: 'retrofitLoft' },
    { key: 'retrofit.wallInsulation',    type: 'enum', required: false, enumRef: 'retrofitWall' },
    { key: 'retrofit.windows',           type: 'enum', required: false, enumRef: 'retrofitWindows' },
    { key: 'retrofit.draughtProofing',   type: 'enum', required: false, enumRef: 'retrofitDraught' },

    // ── Solar ────────────────────────────────────────────────────────────────
    { key: 'solarIntent',                type: 'enum', required: false, enumRef: 'solarIntent' },

    // ── Controls / tariff ────────────────────────────────────────────────────
    { key: 'tariffProfile',              type: 'enum', required: false, enumRef: 'tariffProfile' },
    { key: 'controlsPackagePreference',  type: 'enum', required: false, enumRef: 'controls' },

    // ── Upgrade tracking ─────────────────────────────────────────────────────
    { key: 'requiredUpgradesForBestCase', type: 'array', required: false, enumRef: 'requiredUpgradeFlags' },
  ],
  engineOutputs: [
    'primary',
    'alternatives',
    'scores.total',
    'scores.subscores',
    'year1.runningCost',
    'year1.carbonKg',
    'lifecycle.maintenance',
    'requiredUpgrades',
    'reasons.whyThisWon',
    'rulesetVersion',
  ],
};
